import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutGrid,
  Briefcase,
  FileText,
  Receipt,
  UserCircle2,
  LogOut,
  Plus,
  X,
  ShoppingBag,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile } from '../../services/userService';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  end?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/app', label: 'Dashboard', icon: LayoutGrid, end: true },
  { to: '/app/plans', label: 'My Plans', icon: Briefcase },
  { to: '/app/shopping', label: 'Shopping Sessions', icon: ShoppingBag },
  { to: '/app/reports', label: 'Reports', icon: FileText },
  { to: '/app/orders', label: 'Orders & Invoices', icon: Receipt },
  { to: '/app/profile', label: 'Style Profile', icon: UserCircle2 },
];

const AppSidebar: React.FC<Props> = ({ open, onClose }) => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  // Avatar: prefer the auth provider photo (Google), then the photo uploaded
  // during profile completion, then fall back to initials.
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  useEffect(() => {
    let mounted = true;
    if (!currentUser?.uid) { setProfilePhoto(null); return; }
    getUserProfile(currentUser.uid)
      .then((p) => { if (mounted) setProfilePhoto(p?.pendingFacePhotoUrl || null); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [currentUser?.uid]);
  const avatarUrl = currentUser?.photoURL || profilePhoto;

  const onLogout = async () => {
    await logout();
    navigate('/');
  };

  const initials = (() => {
    const name = currentUser?.displayName || currentUser?.email || '';
    const cleaned = name.trim();
    if (!cleaned) return 'U';
    const parts = cleaned.split(/[\s@]+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const second = parts.length > 1 ? parts[1][0] : '';
    return (first + second).toUpperCase() || cleaned[0].toUpperCase();
  })();

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`lg:hidden fixed inset-0 z-30 bg-charcoal/40 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className={`fixed top-0 left-0 z-40 h-screen w-72 bg-white border-r border-light-gray flex flex-col transform transition-transform duration-300 lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="App navigation"
      >
        {/* Header / brand */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-light-gray">
          <Link
            to="/app"
            onClick={onClose}
            className="flex items-center gap-3 group"
          >
            <img
              src="/logo.png"
              alt="Suitcase Muse"
              className="w-10 h-10 rounded-xl transition-transform group-hover:scale-105"
            />
            <div className="leading-tight">
              <div className="font-nunito font-bold text-base text-charcoal">Suitcase Muse</div>
              <div className="text-xs text-gray-500">Your style space</div>
            </div>
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg text-gray-500 hover:text-charcoal hover:bg-light-gray transition-colors"
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        {/* Primary CTA */}
        <div className="px-4 pt-4">
          <Link
            to="/app/trips/new"
            onClick={onClose}
            className="flex items-center justify-center w-full gap-2 px-4 py-3 rounded-2xl bg-gradient-to-r from-coral to-coral-light text-white font-semibold hover:shadow-glow-coral transition-all duration-300"
          >
            <Plus size={18} />
            New Plan
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 px-3 mb-2">
            Workspace
          </div>
          <ul className="space-y-0.5">
            {NAV_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-coral/10 text-coral'
                          : 'text-charcoal hover:bg-light-gray'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon
                          size={18}
                          className={isActive ? 'text-coral' : 'text-gray-500'}
                        />
                        <span>{item.label}</span>
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 px-3 mt-6 mb-2">
            More
          </div>
          <ul className="space-y-0.5">
            <li>
              <Link
                to="/app/services"
                onClick={onClose}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-charcoal hover:bg-light-gray transition-colors"
              >
                <Briefcase size={18} className="text-gray-500" />
                Browse Services
              </Link>
            </li>
          </ul>
        </nav>

        {/* User footer */}
        <div className="border-t border-light-gray p-4">
          <Link
            to="/app/profile"
            onClick={onClose}
            className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-light-gray transition-colors mb-2"
          >
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="You"
                referrerPolicy="no-referrer"
                className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-light-gray"
                onError={(e) => {
                  // Fall back to initials if the avatar fails to load.
                  const img = e.currentTarget as HTMLImageElement;
                  const fallback = img.nextElementSibling as HTMLElement | null;
                  img.style.display = 'none';
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="w-10 h-10 rounded-full bg-gradient-to-br from-coral to-teal text-white flex items-center justify-center font-semibold text-sm flex-shrink-0"
              style={{ display: avatarUrl ? 'none' : 'flex' }}
            >
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-charcoal truncate">
                {currentUser?.displayName || 'My Account'}
              </div>
              <div className="text-xs text-gray-500 truncate">
                {currentUser?.email || currentUser?.phoneNumber || ''}
              </div>
            </div>
          </Link>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-coral hover:bg-coral/5 transition-colors"
          >
            <LogOut size={18} />
            Log out
          </button>
        </div>
      </aside>
    </>
  );
};

export default AppSidebar;
